import Icon from '@/components/Icon'
import { Syllabus, SyllabusExternal } from '@/kontent/content-types'
import { isAllowPreviewExternalSyllabus, isYes } from '@/utils'
import { getSyllabusUrlFromMappingBySyllabusCodename } from '@/utils/getSyllabusUrlFromMapping'
import { getSyllabusTitleWithPublicationYear } from '@/utils/syllabus/getSyllabusTitleWithPublicationYear'
import { isSyllabusExternal } from '@/utils/type_predicates'
import React, { useEffect, useId, useState } from 'react'
import { useKontentHomeConfig } from '../contexts/KontentHomeConfigProvider'
import Link from '../Link'
import SyllabusExternalLink from '../SyllabusExternalLink'

export interface IProps {
	syllabuses: (Syllabus | SyllabusExternal)[]
	isActive?: boolean
	titleBlank?: string
}

// is Redirect = "Redirect to education standard" - (doredirect)
// Allow Staging view (allowpreview)
const SyllabusLink = ({
	syllabus,
}: {
	syllabus: Syllabus | SyllabusExternal
}) => {
	const { mappings } = useKontentHomeConfig()
	let syllabusLabel = getSyllabusTitleWithPublicationYear(syllabus, false)

	// if syllabus is external, always show external link
	if (isSyllabusExternal(syllabus)) {
		return <SyllabusExternalLink item={syllabus} />
	}

	const isRedirectSyllabus = isYes(syllabus.elements.doredirect)
	const isAllowStagingView = isYes(syllabus.elements.allowpreview)
	const isOnNewStagingSite = isAllowPreviewExternalSyllabus()

	if (
		!isRedirectSyllabus ||
		(isRedirectSyllabus && isAllowStagingView && isOnNewStagingSite)
	) {
		// Use canonical KLA URL - syllabuses with multiple KLAs always link
		// to the canonical KLA (key_learning_area_default field)
		// Pass false for isCanonicalityChecked since kla_default handles canonical URL
		let url = getSyllabusUrlFromMappingBySyllabusCodename(
			mappings,
			syllabus.system.codename,
			false,
		)

		if (!url) return <span>{syllabusLabel}</span>
		return (
			<Link href={url} className="inline-flex items-center">
				<span>{syllabusLabel}</span>
				<Icon icon={'mdi:chevron-right'} className="flex-shrink-0" />
			</Link>
		)
	}

	// if it has redirect url
	if (syllabus.elements.redirecturl.value) {
		// link to external link
		return <SyllabusExternalLink item={syllabus} />
	}
	// display as title
	return <span>{syllabusLabel}</span>
}

function SyllabusListShowHide({ syllabuses, isActive, titleBlank }: IProps) {
	const [show, setShow] = useState(isActive ? isActive : false)
	const id = useId()

	useEffect(() => {
		setShow(isActive)
	}, [isActive])

	return (
		<div>
			{syllabuses.length == 1 && (
				<SyllabusLink syllabus={syllabuses[0]} />
			)}
			{syllabuses.length > 1 && (
				<div>
					<div className="font-bold">Multiple Syllabuses</div>
					{syllabuses.length > 2 ? (
						<div className="richtext">
							<ul className="mt-2">
								{syllabuses.map((syllabus, index) => {
									return (
										<React.Fragment
											key={syllabus.system.id + '-' + id}
										>
											{(show || index < 2) && (
												<li className="mt-0">
													<SyllabusLink
														syllabus={syllabus}
													/>
												</li>
											)}
										</React.Fragment>
									)
								})}
							</ul>
						</div>
					) : (
						<div className="richtext">
							<ul className="mt-2">
								{syllabuses.map((syllabus) => {
									return (
										<li
											key={syllabus.system.id}
											className="mt-0"
										>
											<SyllabusLink syllabus={syllabus} />
										</li>
									)
								})}
							</ul>
						</div>
					)}
				</div>
			)}
			{syllabuses.length == 0 && <span>{titleBlank}</span>}
			{syllabuses.length > 2 && (
				<button
					onClick={() => setShow(!show)}
					type="button"
					className="mt-3 text-[#002664] font-bold underline cursor-pointer"
				>
					{show ? 'Hide syllabuses' : 'Show all syllabuses'}
					{show ? (
						<Icon icon={'mdi:chevron-up'} />
					) : (
						<Icon icon={'mdi:chevron-down'} />
					)}
				</button>
			)}
		</div>
	)
}

export default SyllabusListShowHide
